/**
 * AISearchBar
 *
 * Natural-language property search powered by Claude Sonnet 4.6.
 * Users type ("3 bed house under 300k in Wicklow"), speak (mic
 * button) or tap an example pill, and we route them to /buy, /rent
 * or /map with filters / commute polygon already applied.  Signed-in
 * users can also save the phrase as a named search + email alert.
 */
import React, { useState, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, Loader2, ArrowRight, X, Mic, MicOff, Bell, CheckCircle2 } from 'lucide-react';
import { useMarket } from '../hooks/useMarket';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/* ------------------------------------------------------------------ */
/* URL builders                                                        */
/* ------------------------------------------------------------------ */
const buildListingHref = (route, parsed) => {
  const params = new URLSearchParams();
  if (parsed.property_type)          params.set('propertyType', parsed.property_type);
  if (parsed.bedrooms_min)           params.set('bedsMin',      String(parsed.bedrooms_min));
  if (parsed.bedrooms_max)           params.set('bedsMax',      String(parsed.bedrooms_max));
  if (parsed.bathrooms_min)          params.set('bathsMin',     String(parsed.bathrooms_min));
  if (parsed.price_min)              params.set('priceMin',     String(parsed.price_min));
  if (parsed.price_max)              params.set('priceMax',     String(parsed.price_max));
  if (parsed.ber_min)                params.set('berMin',       parsed.ber_min);
  if (parsed.size_min)               params.set('sizeMin',      String(parsed.size_min));
  if (parsed.area || parsed.county)  params.set('q',            parsed.area || parsed.county);
  const qs = params.toString();
  return `${route.page}${qs ? `?${qs}` : ''}`;
};

const buildCommuteHref = (parsed) => {
  const params = new URLSearchParams();
  params.set('commuteTo',   parsed.commute_to || '');
  params.set('commuteMins', String(parsed.commute_mins || 30));
  params.set('commuteMode', parsed.commute_mode || 'transit');
  if (parsed.property_type) params.set('propertyType', parsed.property_type);
  if (parsed.listing_type)  params.set('type',         parsed.listing_type);
  if (parsed.price_max)     params.set('priceMax',     String(parsed.price_max));
  if (parsed.bedrooms_min)  params.set('bedsMin',      String(parsed.bedrooms_min));
  return `/map?${params.toString()}`;
};

// Nearby-amenity queries also route to /map so we can render pins and
// a walk/drive-buffer polygon around each match.  The `nearby` array
// is JSON-encoded so MapSearchPage can preserve every field without
// URL param explosion.
const buildNearbyHref = (parsed) => {
  const params  = new URLSearchParams();
  const nbList  = Array.isArray(parsed.nearby) ? parsed.nearby : [parsed.nearby];
  params.set('nearby', JSON.stringify(nbList));
  if (parsed.county)         params.set('area',         parsed.county);
  else if (parsed.area)      params.set('area',         parsed.area);
  if (parsed.property_type)  params.set('propertyType', parsed.property_type);
  if (parsed.listing_type)   params.set('type',         parsed.listing_type);
  if (parsed.price_max)      params.set('priceMax',     String(parsed.price_max));
  if (parsed.price_min)      params.set('priceMin',     String(parsed.price_min));
  if (parsed.bedrooms_min)   params.set('bedsMin',      String(parsed.bedrooms_min));
  return `/map?${params.toString()}`;
};

/* ------------------------------------------------------------------ */
/* Parsed → saved-search filters bridge                                */
/*   Existing /api/saved-searches accepts a different shape than the   */
/*   AI parser emits — this maps between them so alerts work.          */
/* ------------------------------------------------------------------ */
const parsedToSavedFilters = (parsed) => {
  const f = {};
  if (parsed.listing_type)   f.type          = parsed.listing_type;
  if (parsed.property_type)  f.propertyType  = parsed.property_type;
  if (parsed.county)         f.county        = parsed.county;
  if (parsed.area)           f.area          = parsed.area;
  if (parsed.price_min != null) f.minPrice   = parsed.price_min;
  if (parsed.price_max != null) f.maxPrice   = parsed.price_max;
  if (parsed.bedrooms_min != null) f.minBedrooms = parsed.bedrooms_min;
  if (parsed.bedrooms_max != null) f.maxBedrooms = parsed.bedrooms_max;
  return f;
};

/* ------------------------------------------------------------------ */
/* Example query pills                                                 */
/*   Market-aware so buyers see currency + local place names.          */
/* ------------------------------------------------------------------ */
const EXAMPLES = {
  IE: [
    '3 bed house under €400k in Cork',
    'Apartment to rent near Trinity College',
    '40 min transit of Grand Canal Dock',
    'BER B1+ family homes in Ranelagh',
  ],
  GB: [
    '3 bed under £500k in Manchester',
    'Flat to rent within 30 mins of King\u2019s Cross',
    'EPC B+ house near Clapham Common',
    'New-build houses in Reading, £600k max',
  ],
  US: [
    '3 bed house under $500k in Austin',
    'Apartment to rent within 30 mins of downtown Boston',
    'Family home with garden in Brooklyn',
    'Condo under $800k in Miami Beach',
  ],
};

const detectMarket = () => {
  try {
    const path = window.location.pathname.toLowerCase();
    if (path.startsWith('/uk')) return 'GB';
    if (path.startsWith('/us') || path.startsWith('/usa')) return 'US';
  } catch { /* SSR */ }
  return 'IE';
};

// Voice-recognition locale per market so Irish, British and American
// accents transcribe more reliably.  Browsers all accept BCP-47 tags.
const VOICE_LOCALES = {
  IE: 'en-IE',
  GB: 'en-GB',
  US: 'en-US',
};

/* ------------------------------------------------------------------ */
/* Local search history (last N successful queries)                    */
/* ------------------------------------------------------------------ */
const HISTORY_KEY = 'lh:ai-search-history';
const HISTORY_MAX = 5;

const readHistory = () => {
  try {
    const raw = localStorage.getItem(HISTORY_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr.slice(0, HISTORY_MAX) : [];
  } catch { return []; }
};

const pushHistory = (text) => {
  try {
    const clean = (text || '').trim();
    if (!clean) return;
    const cur = readHistory().filter((h) => h.toLowerCase() !== clean.toLowerCase());
    cur.unshift(clean);
    localStorage.setItem(HISTORY_KEY, JSON.stringify(cur.slice(0, HISTORY_MAX)));
  } catch { /* quota / private mode — non-fatal */ }
};

const removeHistoryItem = (text) => {
  try {
    const cur = readHistory().filter((h) => h !== text);
    localStorage.setItem(HISTORY_KEY, JSON.stringify(cur));
  } catch { /* non-fatal */ }
};

/* ------------------------------------------------------------------ */
/* Component                                                           */
/* ------------------------------------------------------------------ */
export const AISearchBar = ({
  placeholder,
  className   = '',
  darkOnLight = false,
  testId      = 'ai-search-bar',
  showExamples = true,
}) => {
  const market = useMarket();
  const effectivePlaceholder = placeholder || market.search_placeholder || 'e.g. 3 bed house under €300k in Wicklow, or 40 min commute of Citywest';
  const [q, setQ]                 = useState('');
  const [busy, setBusy]           = useState(false);
  const [result, setResult]       = useState(null);
  const [error, setError]         = useState(null);
  const [listening, setListening] = useState(false);
  const [saving, setSaving]       = useState(false);
  const [saved, setSaved]         = useState(false);
  const [history, setHistory]     = useState(readHistory);
  const recogRef                  = useRef(null);
  const inputRef                  = useRef(null);
  const navigate                  = useNavigate();

  const marketCode = market.isUK ? 'GB' : market.isUS ? 'US' : 'IE';
  const examples   = useMemo(() => EXAMPLES[marketCode] || EXAMPLES.IE, [marketCode]);
  const speechAvailable = typeof window !== 'undefined'
    && (window.SpeechRecognition || window.webkitSpeechRecognition);

  /* --- parse ----------------------------------------------------- */
  const parse = async (raw) => {
    const text = (raw ?? q ?? '').trim();
    if (!text || text.length < 3) return;
    setBusy(true); setError(null); setResult(null); setSaved(false);
    try {
      const r = await fetch(`${BACKEND_URL}/api/ai/parse-search`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ query: text }),
      });
      if (!r.ok) throw new Error(`Parse failed (${r.status})`);
      const d = await r.json();
      if (!d.parsed || Object.keys(d.parsed).length === 0) {
        setError("Sorry, I couldn't parse that — try adding an area or a price.");
        return;
      }
      setResult(d);
      pushHistory(text);
      setHistory(readHistory());
    } catch (e) {
      setError(e.message || 'Search failed');
    } finally {
      setBusy(false);
    }
  };

  /* --- confirm & navigate --------------------------------------- */
  const confirm = () => {
    if (!result) return;
    let href;
    if (result.route.target === 'commute')     href = buildCommuteHref(result.parsed);
    else if (result.route.target === 'nearby') href = buildNearbyHref(result.parsed);
    else                                        href = buildListingHref(result.route, result.parsed);
    // Preserve the current market context so a buyer on /uk/ stays
    // on /uk/buy after the AI search — otherwise they'd bleed back
    // to the Ireland default site.
    navigate(market.withPrefix(href));
  };

  /* --- save & alert --------------------------------------------- */
  const saveAndAlert = async () => {
    if (!result) return;
    const token = localStorage.getItem('token');
    if (!token) {
      setError('Please sign in to save this search and get email alerts.');
      return;
    }
    setSaving(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/saved-searches`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization:   `Bearer ${token}`,
        },
        body: JSON.stringify({
          name:          q.trim().slice(0, 80),
          filters:       parsedToSavedFilters(result.parsed),
          email_alerts:  true,
        }),
      });
      if (!r.ok) throw new Error(`Save failed (${r.status})`);
      setSaved(true);
    } catch (e) {
      setError(e.message || 'Could not save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  /* --- voice input (Web Speech API) ----------------------------- */
  const toggleVoice = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;
    if (listening && recogRef.current) {
      try { recogRef.current.stop(); } catch { /* noop */ }
      return;
    }
    const r = new SR();
    r.lang = VOICE_LOCALES[marketCode] || 'en-GB';
    r.interimResults = true;
    r.continuous = false;
    r.onresult = (ev) => {
      let transcript = '';
      for (let i = ev.resultIndex; i < ev.results.length; i++) {
        transcript += ev.results[i][0].transcript;
      }
      setQ(transcript);
      if (ev.results[ev.results.length - 1].isFinal) {
        // Auto-submit shortly after final transcript.
        setTimeout(() => parse(transcript), 350);
      }
    };
    r.onerror = () => setListening(false);
    r.onend   = () => setListening(false);
    recogRef.current = r;
    setListening(true);
    try { r.start(); } catch { setListening(false); }
  };

  /* --- misc ----------------------------------------------------- */
  const clear = () => { setQ(''); setResult(null); setError(null); setSaved(false); };
  const onKey = (e) => { if (e.key === 'Enter') { e.preventDefault(); parse(); } };
  const pickExample = (text) => { setQ(text); parse(text); };

  const inputCls = darkOnLight
    ? 'bg-white text-gray-900 placeholder:text-gray-500 ring-gray-200'
    : 'bg-white/95 text-gray-900 placeholder:text-gray-500 ring-white/60';

  return (
    <div className={`w-full ${className}`} data-testid={testId}>
      <div className={`flex items-center gap-2 rounded-2xl ring-1 shadow-lg px-3 py-2 ${inputCls}`}>
        <Sparkles className="h-5 w-5 text-[#7042cc] shrink-0" />
        <input
          ref={inputRef}
          type="text"
          value={q}
          onChange={(e) => { setQ(e.target.value); if (result) setResult(null); if (error) setError(null); if (saved) setSaved(false); }}
          onKeyDown={onKey}
          placeholder={effectivePlaceholder}
          className="flex-1 min-w-0 bg-transparent border-0 focus:outline-none text-sm sm:text-[15px]"
          data-testid={`${testId}-input`}
          aria-label="Ask in your own words"
        />
        {q && !busy && (
          <button
            type="button"
            onClick={clear}
            className="p-1 rounded hover:bg-gray-100 text-gray-400"
            data-testid={`${testId}-clear`}
            aria-label="Clear"
          >
            <X className="h-4 w-4" />
          </button>
        )}
        {speechAvailable && (
          <button
            type="button"
            onClick={toggleVoice}
            className={`p-1.5 rounded-full transition-colors ${listening
              ? 'bg-red-100 text-red-600 animate-pulse'
              : 'text-gray-400 hover:text-[#7042cc] hover:bg-gray-100'}`}
            title={listening ? 'Stop listening' : 'Search by voice'}
            data-testid={`${testId}-mic`}
            aria-label={listening ? 'Stop listening' : 'Search by voice'}
          >
            {listening ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
          </button>
        )}
        <button
          type="button"
          onClick={() => parse()}
          disabled={busy || q.trim().length < 3}
          className="inline-flex items-center gap-1.5 h-9 px-4 rounded-xl bg-[#7042cc] hover:bg-[#5b35a8] disabled:bg-gray-300 text-white text-sm font-semibold whitespace-nowrap"
          data-testid={`${testId}-submit`}
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Ask AI <ArrowRight className="h-4 w-4" /></>}
        </button>
      </div>

      {/* Recent searches — one-tap re-run of the user's last 5
          successful AI queries (kept in localStorage). Shown ABOVE
          the examples when history exists so the more useful list
          comes first. */}
      {showExamples && !result && !busy && history.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5 items-center" data-testid={`${testId}-history`}>
          <span className="text-[11px] text-white/80 mr-1">Recent:</span>
          {history.map((h) => (
            <span
              key={h}
              className="inline-flex items-center gap-1 text-[11px] pl-2.5 pr-1 py-1 rounded-full bg-white/25 hover:bg-white/35 text-white ring-1 ring-white/40 backdrop-blur transition-colors"
            >
              <button
                type="button"
                onClick={() => pickExample(h)}
                data-testid={`${testId}-history-item`}
                className="max-w-[240px] truncate"
                title={`Re-run: ${h}`}
              >
                {h}
              </button>
              <button
                type="button"
                onClick={() => { removeHistoryItem(h); setHistory(readHistory()); }}
                className="p-0.5 rounded-full hover:bg-white/25 text-white/80 hover:text-white"
                aria-label={`Remove ${h}`}
                data-testid={`${testId}-history-remove`}
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Example pills — help first-timers learn what to type. */}
      {showExamples && !result && !busy && (
        <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`${testId}-examples`}>
          <span className="text-[11px] text-white/80 self-center mr-1">Try:</span>
          {examples.map((ex) => (
            <button
              key={ex}
              type="button"
              onClick={() => pickExample(ex)}
              className="text-[11px] px-2.5 py-1 rounded-full bg-white/20 hover:bg-white/30 text-white ring-1 ring-white/30 backdrop-blur transition-colors"
              data-testid={`${testId}-example`}
            >
              {ex}
            </button>
          ))}
        </div>
      )}

      {/* Interpreted-filter preview + actions */}
      {result && (
        <div
          className="mt-2 rounded-xl bg-white/95 backdrop-blur ring-1 ring-[#7042cc]/20 px-3 py-2.5 flex items-center gap-2 flex-wrap"
          data-testid={`${testId}-result`}
        >
          <span className="text-[11px] font-semibold uppercase tracking-widest text-[#7042cc] mr-1">
            Got it —
          </span>
          {result.chips.length === 0
            ? <span className="text-xs text-gray-600">nothing to filter — searching all listings</span>
            : result.chips.map((c) => (
                <span key={c} className="inline-flex items-center h-6 px-2.5 rounded-full text-[11px] font-semibold bg-[#7042cc]/10 text-[#7042cc] ring-1 ring-[#7042cc]/20">
                  {c}
                </span>
              ))
          }
          <div className="ml-auto flex items-center gap-1.5">
            <button
              type="button"
              onClick={saveAndAlert}
              disabled={saving || saved}
              className={`inline-flex items-center gap-1 h-7 px-2.5 rounded-full text-[11px] font-semibold ring-1 transition-colors ${saved
                ? 'bg-emerald-50 text-emerald-700 ring-emerald-200'
                : 'bg-white text-[#7042cc] ring-[#7042cc]/30 hover:bg-[#7042cc]/10'}`}
              data-testid={`${testId}-save`}
              title={saved ? 'Saved — we\u2019ll email you new matches' : 'Save this search & email me new matches'}
            >
              {saving
                ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                : saved
                  ? <CheckCircle2 className="h-3.5 w-3.5" />
                  : <Bell className="h-3.5 w-3.5" />}
              {saved ? 'Saved · alerts on' : 'Save & alert me'}
            </button>
            <button
              type="button"
              onClick={confirm}
              className="inline-flex items-center gap-1 h-7 px-3 rounded-full bg-[#7042cc] hover:bg-[#5b35a8] text-white text-[11px] font-bold shadow-sm"
              data-testid={`${testId}-confirm`}
            >
              Show matches
              <ArrowRight className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="mt-2 text-[11px] text-amber-700 bg-amber-50 ring-1 ring-amber-200 rounded-md px-2.5 py-1.5"
              data-testid={`${testId}-error`}>
          {error}
        </div>
      )}
    </div>
  );
};

export default AISearchBar;
